/* eslint-disable react/display-name */
import React, { useMemo, useState } from 'react';
import Styles from './TextField.css';
import clsx from 'clsx';
import dynamic from 'next/dynamic';

const InputNumberFormat = dynamic(
  () => import('@react-input/number-format').then((mod) => mod.InputNumberFormat),
  { ssr: true, loading: () => <input className="text-field__input" readOnly /> },
);

export const MoneyTextFieldEmptyValue = '00,00';

export const EmailSuggestions = [
  ...(process.env.NODE_ENV === 'development'
    ? [{ label: '@example.com', value: '@example.com' }]
    : []),
  { label: '@gmail.com', value: '@gmail.com' },
  { label: '@hotmail.com', value: '@hotmail.com' },
  { label: '@outlook.com', value: '@outlook.com' },
  { label: '@icloud.com', value: '@icloud.com' },
  { label: '@yahoo.com', value: '@yahoo.com' },
];

/**
 * @type {import('../../../../../types/SimuladorEnergiaSolar').TextFieldComponent}
 */
const TextField = React.forwardRef(
  (
    {
      label,
      error,
      helperText,
      maskFunction,
      handleValueChange,
      startAdornment,
      endAdornment,
      children,
      // eslint-disable-next-line no-unused-vars
      extraLabel,
      // eslint-disable-next-line no-unused-vars
      extraLabelHtml,
      hideLabelOverflow = false,
      variant = 'default',
      ...props
    },
    ref,
  ) => {
    const [focused, setFocused] = useState(false);
    /** @type {React.MutableRefObject<InputNumberFormat>} */

    const isFloating =
      focused || (props.value !== undefined && props.value !== null && props.value !== '');

    const isMoney = props.type === 'money' && variant === 'default';
    const showMoneySign = props.type === 'money' && isFloating;
    const showMoneyClear =
      props.type === 'money' && !!props.value && props.value !== MoneyTextFieldEmptyValue;
    const hideLabel = isMoney || !label;

    const showEmailSuggestions = useMemo(() => {
      if (props.type !== 'email') return false;

      const value = `${props.value || ''}`.trim();

      const stopAt = value.endsWith('@');

      if (stopAt) return true;
      const match = value.split('@')[1];

      if (match) {
        return EmailSuggestions.some((s) => s.value.startsWith(`@${match}`));
      }

      return false;
    }, [props.type, props.value]);

    const renderEmailSuggestions = useMemo(() => {
      if (!showEmailSuggestions) return null;

      const value = `${props.value || ''}`.trim();
      const mail = value.split('@')[1];
      const suggestions = EmailSuggestions.filter((s) => s.value.startsWith(`@${mail}`));

      if (suggestions.length === 1) {
        if (value.toLowerCase().endsWith(suggestions[0].value)) {
          return [];
        }
      }

      return suggestions;
    }, [showEmailSuggestions, props.value]);

    const handleMoneyFocus = (ev, hasFocus = false) => {
      const value = ev?.target?.value;

      if (!value || value === MoneyTextFieldEmptyValue) {
        const changeEvent = new Event('change', { bubbles: true });
        Object.defineProperty(changeEvent, 'target', {
          writable: false,
          value: { ...ev.target, value: hasFocus ? '' : MoneyTextFieldEmptyValue },
        });

        if (props.onChange) {
          props.onChange(changeEvent);
        }
      }
    };

    const handleMoneyClear = () => {
      if (!props.onChange) return;

      const element = document.getElementById(props.id || props.name);
      if (!element) return;

      const changeEvent = new Event('change', { bubbles: true });
      Object.defineProperty(changeEvent, 'target', {
        writable: false,
        value: { ...element, value: MoneyTextFieldEmptyValue },
      });

      props.onChange(changeEvent);
      requestAnimationFrame(() => {
        element.focus();
        element.setSelectionRange(0, 0);
      });
    };

    const processInputChange = (ev) => {
      let value = ev?.target?.value;

      if (maskFunction) {
        value = maskFunction(value, props.value);

        if (ev?.target) {
          ev.target.value = value;
        }
      }

      return ev;
    };

    /**
     *
     * @param {React.KeyboardEvent<HTMLInputElement>} event
     * @param {string} nextValue
     */
    const dispatchMoneyKeyEvent = (event, nextValue = '', isInitial = false) => {
      event.preventDefault();

      const input = event.currentTarget;
      const inputValueSetter = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        'value',
      )?.set;

      if (inputValueSetter) {
        inputValueSetter.call(input, nextValue);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        input.value = nextValue;
        if (props.onChange) {
          props.onChange(processInputChange({ ...event, target: input, currentTarget: input }));
        }
      }

      requestAnimationFrame(() => {
        if (isInitial) {
          input.setSelectionRange(1, 1);
        } else {
          input.setSelectionRange(nextValue.length - 1, nextValue.length - 1);
        }
      });
    };

    /**
     * @param {React.KeyboardEvent<HTMLInputElement>} event
     */
    const handleMoneyTraillingInput = (event) => {
      const value = event.currentTarget.value || '';

      if (!value || !value.includes(',')) return;
      const decimalPart = value.split(',')[1];
      if (decimalPart.length < 2) return;
      const isValidKey = new RegExp('[0-9]+$').test(event.key);
      if (!isValidKey) return;

      const isCursorAtEnd = event.currentTarget.selectionStart === value.length;
      if (!isCursorAtEnd) return;

      const n = value.split(',');
      dispatchMoneyKeyEvent(event, `${n[0]},${event.key}${n[1].slice(1)}`);
    };

    return (
      <div
        className={clsx({
          'text-field': true,
          focused,
          error,
          disabled: props.disabled,
          'show-money-sign': showMoneySign,
          'has-start-adornment': !!startAdornment,
          'has-end-adornment': !!endAdornment,
          'is-money': isMoney,
          'hide-label': hideLabel,
          [`${variant}-variant`]: true,
        })}
      >
        <div className="text-field__container">
          <label
            className={clsx(
              `text-field__label${isFloating ? ' floating' : ''}`,
              hideLabelOverflow && 'hide-label-overflow',
            )}
            htmlFor={props.id || props.name}
          >
            {label}
          </label>

          {props.type !== 'money' && (
            <input
              {...props}
              ref={ref}
              type={props.type === 'email' ? 'text' : props.type || 'text'}
              id={props.id || props.name}
              className="text-field__input"
              onChange={(ev) => {
                if (!props.onChange) return;
                props.onChange(processInputChange(ev));
              }}
              onFocus={(ev) => {
                setFocused(true);
                if (!props.onFocus) return;
                props.onFocus(processInputChange(ev));
              }}
              onBlur={(ev) => {
                setFocused(false);
                if (!props.onBlur) return;
                props.onBlur(processInputChange(ev));
              }}
              aria-autocomplete={props.type === 'email' ? 'none' : props['aria-autocomplete']}
              autoComplete={props.type === 'email' ? 'off' : props.autoComplete}
              inputMode={props.type === 'email' ? 'email' : props.inputMode}
            />
          )}

          {props.type === 'money' && (
            <InputNumberFormat
              {...props}
              inputMode="decimal"
              minimumFractionDigits={2}
              maximumFractionDigits={2}
              currency="BRL"
              locales="pt-BR"
              type={undefined}
              id={props.id || props.name}
              ref={ref}
              className="text-field__input"
              onKeyDown={handleMoneyTraillingInput}
              onChange={(ev) => {
                if (!props.onChange) return;
                props.onChange(processInputChange(ev));
              }}
              onFocus={(ev) => {
                setFocused(true);
                handleMoneyFocus(ev, true);

                if (!props.onFocus) return;
                props.onFocus(processInputChange(ev));
              }}
              onBlur={(ev) => {
                setFocused(false);

                handleMoneyFocus(ev, false);

                if (!props.onBlur) return;
                props.onBlur(processInputChange(ev));
              }}
              aria-autocomplete="none"
              autoComplete="off"
            />
          )}

          <fieldset className="text-field__fieldset" aria-hidden="true">
            <legend className={`text-field__legend${isFloating ? ' visible' : ''}`}>
              <span>{label}</span>
            </legend>
          </fieldset>

          {showMoneySign && <span className="money-adornment">R$</span>}

          {showMoneyClear && (
            <div className="text-field__end-adornment interactive" onClick={handleMoneyClear}>
              <img src="assets/simulador-energia-solar/jornada/ic-clear.svg" alt="" />
            </div>
          )}

          {startAdornment && <div className="text-field__start-adornment">{startAdornment}</div>}
          {endAdornment && <div className="text-field__end-adornment">{endAdornment}</div>}
        </div>

        {helperText && <p className={`text-field__helper${error ? ' error' : ''}`}>{helperText}</p>}

        {showEmailSuggestions && renderEmailSuggestions.length > 0 && (
          <div className="text-field__suggestions">
            {renderEmailSuggestions.map((s) => {
              return (
                <div
                  className="suggestion"
                  key={`sug-${s.value}`}
                  onClick={() => {
                    if (!handleValueChange) return;

                    const value = `${props.value.split('@')[0]}${s.value}`;

                    if (maskFunction) {
                      handleValueChange(maskFunction(value, props.value));
                      return;
                    }

                    handleValueChange(value);
                  }}
                >
                  <p>{s.label}</p>
                </div>
              );
            })}
          </div>
        )}

        {children}
        <style jsx>{Styles}</style>
      </div>
    );
  },
);

export default TextField;
